import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Progress,
  Spinner,
  Alert,
  Badge,
  UncontrolledTooltip
} from 'reactstrap';
import api from '../../api';
import './EVTraining.css'; // We'll create this file for animations

const EVTraining = ({ pokemonId, onEVsUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [evData, setEvData] = useState(null);
  const [availableCandies, setAvailableCandies] = useState([]);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Stat colors for visual representation
  const statColors = {
    hp: '#FF5959',
    atk: '#F5AC78',
    def: '#FAE078',
    spa: '#9DB7F5',
    spd: '#A7DB8D',
    spe: '#FA92B2'
  };

  // Stat display names
  const statNames = {
    hp: 'HP',
    atk: 'Attack',
    def: 'Defense',
    spa: 'Sp. Attack',
    spd: 'Sp. Defense',
    spe: 'Speed'
  };

  // Candy to stat mapping
  const candyToStat = {
    'Health Candy': 'hp',
    'Might Candy': 'atk',
    'Tough Candy': 'def',
    'Smart Candy': 'spa',
    'Courage Candy': 'spd',
    'Quick Candy': 'spe'
  };
  
  // Candy image mapping
  const candyImages = {
    'Health Candy': '/items/HealthCandy.png',
    'Health Candy L': '/items/HealthCandyL.png',
    'Health Candy XL': '/items/HealthCandyXL.png',
    'Might Candy': '/items/MightyCandy.png',
    'Might Candy L': '/items/MightyCandyL.png',
    'Might Candy XL': '/items/MightyCandyXL.png',
    'Tough Candy': '/items/ToughCandy.png',
    'Tough Candy L': '/items/ToughCandyL.png',
    'Tough Candy XL': '/items/ToughCandyXL.png',
    'Smart Candy': '/items/SmartCandy.png',
    'Smart Candy L': '/items/SmartCandyL.png',
    'Smart Candy XL': '/items/SmartCandyXL.png',
    'Courage Candy': '/items/CourageCandy.png',
    'Courage Candy L': '/items/CourageCandyL.png',
    'Courage Candy XL': '/items/CourageCandyXL.png',
    'Quick Candy': '/items/QuickCandy.png',
    'Quick Candy L': '/items/QuickCandyL.png',
    'Quick Candy XL': '/items/QuickCandyXL.png',
  };

  // Load Pokemon EV data and available candies
  useEffect(() => {
    const fetchData = async () => {
      if (!pokemonId) return;
      
      setLoading(true);
      try {
        // Get Pokemon EV data
        const { data: evResponse } = await api.get(`/v1/training/pokemon-evs/${pokemonId}`, {
          withCredentials: true
        });
        
        // Get available candies
        const { data: candyResponse } = await api.get('/v1/training/available-candies', {
          withCredentials: true
        });
        
        setEvData(evResponse);
        setAvailableCandies(candyResponse.candies);
      } catch (error) {
        console.error('Error fetching EV data:', error);
        setMessage({
          text: 'Failed to load EV training data. Please try again.',
          type: 'danger'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [pokemonId]);

  // Group candies by their base type
  const groupedCandies = availableCandies.reduce((acc, candy) => {
    // Extract base candy name (without size)
    const baseName = candy.name.replace(' XL', '').replace(' L', '');
    
    if (!acc[baseName]) {
      acc[baseName] = [];
    }
    
    acc[baseName].push(candy);
    return acc;
  }, {});

  // EV candy value mapping
  const EV_CANDY_VALUES = {
    1: 1,   // S
    2: 4,   // M
    3: 10,  // L
    4: 1,   // S
    5: 4,   // M
    6: 10,  // L
    7: 1,   // S
    8: 4,   // M
    9: 10,  // L
    10: 1,  // S
    11: 4,  // M
    12: 10, // L
    13: 1,  // S
    14: 4,  // M
    15: 10, // L
    16: 1,  // S
    17: 4,  // M
    18: 10  // L
  };

  // Create animation for stat increase
  const createStatAnimation = (stat, value) => {
    const statElement = document.getElementById(`ev-stat-${stat}`);
    if (!statElement) return;
    
    // Create particles
    for (let i = 0; i < value; i++) {
      const particle = document.createElement('div');
      particle.className = 'ev-particle';
      particle.style.backgroundColor = statColors[stat];
      
      // Random position within the stat element
      const left = Math.random() * statElement.offsetWidth;
      const top = Math.random() * statElement.offsetHeight;
      particle.style.left = `${left}px`;
      particle.style.top = `${top}px`;
      
      // Add to stat element
      statElement.appendChild(particle);
      
      // Remove after animation completes
      setTimeout(() => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      }, 1000);
    }
    
    // Create value increase indicator
    const valueIncrease = document.createElement('div');
    valueIncrease.className = 'ev-value-increase';
    valueIncrease.textContent = `+${value}`;
    valueIncrease.style.color = statColors[stat];
    
    // Position near the stat value
    const statValueElement = statElement.querySelector('.d-flex span:last-child');
    if (statValueElement) {
      const rect = statValueElement.getBoundingClientRect();
      const parentRect = statElement.getBoundingClientRect();
      
      valueIncrease.style.left = `${rect.left - parentRect.left + rect.width / 2}px`;
      valueIncrease.style.top = `${rect.top - parentRect.top}px`;
      
      statElement.appendChild(valueIncrease);
      
      // Remove after animation completes
      setTimeout(() => {
        if (valueIncrease.parentNode) {
          valueIncrease.parentNode.removeChild(valueIncrease);
        }
      }, 1200);
    }
  };
  
  // Create candy consumption animation
  const createCandyAnimation = (candyName, buttonElement, stat) => {
    if (!buttonElement) return;
    
    // Get button position
    const buttonRect = buttonElement.getBoundingClientRect();
    const parentElement = buttonElement.closest('.tab-pane');
    if (!parentElement) return;
    
    // Create effect container
    const effectContainer = document.createElement('div');
    effectContainer.className = 'candy-effect-container';
    parentElement.style.position = 'relative';
    parentElement.appendChild(effectContainer);
    
    // Create candy image animation
    const candyImage = document.createElement('img');
    candyImage.className = 'candy-image-animation';
    candyImage.src = candyImages[candyName];
    candyImage.alt = candyName;
    
    // Position at button location
    const parentRect = parentElement.getBoundingClientRect();
    candyImage.style.left = `${buttonRect.left - parentRect.left + (buttonRect.width / 2) - 16}px`;
    candyImage.style.top = `${buttonRect.top - parentRect.top + (buttonRect.height / 2) - 16}px`;
    
    effectContainer.appendChild(candyImage);
    
    // Create sparkle effects
    for (let i = 0; i < 5; i++) {
      const sparkle = document.createElement('div');
      sparkle.className = 'candy-sparkle';
      
      // Position around the candy
      sparkle.style.left = `${buttonRect.left - parentRect.left + (buttonRect.width / 2) - 6 + (Math.random() * 20 - 10)}px`;
      sparkle.style.top = `${buttonRect.top - parentRect.top + (buttonRect.height / 2) - 6 + (Math.random() * 20 - 10)}px`;
      
      // Random delay
      sparkle.style.animationDelay = `${Math.random() * 0.3}s`;
      
      effectContainer.appendChild(sparkle);
    }
    
    // Create particles that move toward the stat bar
    const targetElement = document.getElementById(`ev-bar-${stat}`);
    if (targetElement) {
      const targetRect = targetElement.getBoundingClientRect();
      
      // Calculate end positions (relative to button)
      const xEnd = targetRect.left - buttonRect.left + (targetRect.width / 2);
      const yEnd = targetRect.top - buttonRect.top;
      
      for (let i = 0; i < 15; i++) {
        const particle = document.createElement('div');
        particle.className = 'candy-particle';
        
        // Set color based on stat
        particle.style.backgroundColor = statColors[stat];
        particle.style.boxShadow = `0 0 5px ${statColors[stat]}`;
        
        // Position around the candy
        particle.style.left = `${buttonRect.left - parentRect.left + (buttonRect.width / 2) + (Math.random() * 20 - 10)}px`;
        particle.style.top = `${buttonRect.top - parentRect.top + (buttonRect.height / 2) + (Math.random() * 20 - 10)}px`;
        
        // Set custom properties for the animation end position
        particle.style.setProperty('--x-end', `${xEnd}px`);
        particle.style.setProperty('--y-end', `${yEnd}px`);
        
        // Random delay
        particle.style.animationDelay = `${Math.random() * 0.5}s`;
        
        effectContainer.appendChild(particle);
      }
    }
    
    // Remove container after animations complete
    setTimeout(() => {
      if (effectContainer && effectContainer.parentNode) {
        effectContainer.parentNode.removeChild(effectContainer);
      }
    }, 2000);
  };

  // Apply candy to Pokemon
  const applyCandy = async (candyId, event) => {
    if (!pokemonId || applying) return;
    
    setApplying(true);
    setMessage({ text: '', type: '' });
    
    // Get the button element that was clicked
    const buttonElement = event.currentTarget;
    
    try {
      // Find the candy info before API call
      const candyInfo = availableCandies.find(candy => candy.id === candyId);
      const statType = Object.keys(candyToStat).find(key => 
        candyInfo?.name.includes(key)
      );
      const stat = statType ? candyToStat[statType] : null;
      
      // Start candy animation immediately for better UX
      if (candyInfo && stat) {
        createCandyAnimation(candyInfo.name, buttonElement, stat);
      }
      
      const { data } = await api.post('/v1/training/use-candy', {
        candy_id: candyId,
        pokemon_id: pokemonId
      }, {
        withCredentials: true
      });
      
      // Get the stat that was updated and value
      const updatedStat = candyToStat[data.updated_stat.replace('ev_', '')];
      const candyValue = EV_CANDY_VALUES[candyId];
      
      // Create animation for the stat increase
      createStatAnimation(updatedStat, candyValue);
      
      // Update local EV data
      setEvData({
        ...evData,
        evs: data.evs,
        total_evs: data.total_evs,
        remaining_evs: data.remaining_evs,
        current_stats: data.new_stats
      });
      
      // Update available candies (decrease count)
      setAvailableCandies(prev => 
        prev.map(candy => 
          candy.id === candyId 
            ? { ...candy, amount: candy.amount - 1 }
            : candy
        ).filter(candy => candy.amount > 0)
      );
      
      setMessage({
        text: data.message,
        type: 'success'
      });
      
      // Refresh EV data to ensure UI is up to date
      if (pokemonId) {
        try {
          const { data: refreshedEvData } = await api.get(`/v1/training/pokemon-evs/${pokemonId}`, {
            withCredentials: true
          });
          
          setEvData(refreshedEvData);
        } catch (refreshError) {
          console.error('Error refreshing EV data:', refreshError);
        }
      }
      
      // Notify parent component if needed
      if (onEVsUpdated) {
        onEVsUpdated(data);
      }
    } catch (error) {
      console.error('Error applying candy:', error);
      setMessage({
        text: error.response?.data?.detail || 'Failed to apply candy. Please try again.',
        type: 'danger'
      });
    } finally {
      setTimeout(() => {
        setApplying(false);
      }, 2000); // Give animation time to play
    }
  };

  if (loading) {
    return (
      <Card className="shadow-sm" style={{ background: '#272d39', borderRadius: '12px' }}>
        <CardBody className="text-center">
          <Spinner color="light" />
          <p className="mt-2 text-light">Loading EV training data...</p>
        </CardBody>
      </Card>
    );
  }

  if (!evData) {
    return (
      <Card className="shadow-sm" style={{ background: '#272d39', borderRadius: '12px' }}>
        <CardBody className="text-center">
          <p className="text-light">No EV data available for this Pokémon.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm" style={{ background: '#272d39', borderRadius: '12px' }}>
      <CardBody>
        <CardTitle tag="h5" className="text-white mb-3">
          EV Training
          <Badge 
            color="info" 
            className="ms-2"
            id="ev-total-badge"
          >
            {evData.total_evs} / 510
          </Badge>
          <UncontrolledTooltip target="ev-total-badge">
            Total EVs used across all stats (max 510)
          </UncontrolledTooltip>
        </CardTitle>
        
        {message.text && (
          <Alert color={message.type} className="mb-3">
            {message.text}
          </Alert>
        )}
        
        <div className="mb-4">
          <h6 className="text-white">Current EV Distribution</h6>
          {Object.keys(evData.evs).map(stat => (
            <div key={stat} className="mb-2" id={`ev-stat-${stat}`}>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-white">{statNames[stat]}</span>
                <span className="text-white" style={{ position: 'relative' }}>
                  <span className={evData.evs[stat] === evData.max_per_stat ? 'text-warning' : ''}>
                    {evData.evs[stat]}
                  </span> / {evData.max_per_stat}
                  {evData.evs[stat] === evData.max_per_stat && (
                    <span className="max-indicator">MAX</span>
                  )}
                </span>
              </div>
              <Progress
                value={evData.evs[stat]}
                max={evData.max_per_stat}
                className={`ev-bar ${stat}`}
                style={{ width: '100%' }}
                id={`ev-bar-${stat}`}
              />
            </div>
          ))}
        </div>
        
        {Object.keys(groupedCandies).length > 0 ? (
          <div>
            <h6 className="text-white mb-3">Available Candies</h6>
            <Row>
              {Object.entries(groupedCandies).map(([baseName, candies]) => {
                // Sort candies by value (Regular, L, XL)
                const sortedCandies = [...candies].sort((a, b) => a.value - b.value);
                const stat = candyToStat[baseName];
                const currentEV = evData.evs[stat];
                const maxEV = evData.max_per_stat;
                
                return (
                  <Col xs="12" md="6" lg="4" key={baseName} className="mb-3">
                    <Card className="candy-card" style={{ background: '#1d222e', borderRadius: '8px' }}>
                      <CardBody>
                        <h6 className="text-white">{baseName}</h6>
                        <p className="text-white small">Increases {statNames[stat]} EVs</p>
                        
                        <div className="d-flex flex-column">
                          {sortedCandies.map(candy => {
                            const wouldExceedMax = currentEV + candy.value > maxEV;
                            const wouldExceedTotal = evData.total_evs + candy.value > 510;
                            const disabled = wouldExceedMax || wouldExceedTotal || applying;
                            
                            // Determine tooltip message based on disabled reason
                            let tooltipMessage = '';
                            if (wouldExceedMax) {
                              tooltipMessage = `Would exceed max EVs (${maxEV}) for this stat`;
                            } else if (wouldExceedTotal) {
                              tooltipMessage = `Would exceed total EV limit (510)`;
                            }
                            
                            // Tooltip ID for this specific candy
                            const tooltipId = `candy-tooltip-${candy.id}`;
                            
                            return (
                              <div key={candy.id} className="mb-2">
                                <Button
                                  color="light"
                                  style={{ 
                                    marginRight: '5px', 
                                    marginBottom: '5px',
                                    padding: '8px 12px',
                                    position: 'relative',
                                    overflow: 'visible',
                                    backgroundColor: disabled ? '#3a3f4c' : '#2c3242',
                                    borderColor: statColors[stat],
                                    borderWidth: '2px'
                                  }}
                                  size="sm"
                                  onClick={(e) => applyCandy(candy.id, e)}
                                  disabled={disabled}
                                  id={disabled && tooltipMessage ? tooltipId : undefined}
                                  className="candy-btn"
                                >
                                  <div className="d-flex align-items-center">
                                    <img 
                                      src={candyImages[candy.name]} 
                                      alt={candy.name}
                                      style={{ 
                                        width: '24px', 
                                        height: '24px', 
                                        marginRight: '8px',
                                        filter: disabled ? 'grayscale(80%)' : 'none'
                                      }} 
                                    />
                                    <div>
                                      <span className="text-white">+{candy.value}</span>
                                      <span className="text-white ms-2">x{candy.amount}</span>
                                    </div>
                                  </div>
                                </Button>
                                {disabled && tooltipMessage && (
                                  <UncontrolledTooltip placement="top" target={tooltipId}>
                                    {tooltipMessage}
                                  </UncontrolledTooltip>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </div>
        ) : (
          <Alert color="warning">
            No EV candies available. Purchase some from the Trainer Arsenal shop!
          </Alert>
        )}
        
        <div className="mt-4" style={{ 
          backgroundColor: "rgba(0, 0, 0, 0.2)",
          padding: "15px",
          borderRadius: "8px",
          border: "1px solid rgba(255, 255, 255, 0.1)"
        }}>
          <h5 style={{ 
            color: "#FFD700", 
            borderBottom: "1px solid rgba(255, 215, 0, 0.3)",
            paddingBottom: "8px",
            marginBottom: "12px"
          }}>
            <i className="ri-sword-line me-1"></i> Current Stats
          </h5>
          <Row>
            {Object.entries(evData.current_stats).map(([stat, value]) => (
              <Col xs="6" md="4" key={stat} className="mb-2">
                <div className="d-flex justify-content-between">
                  <span className="text-white">{statNames[stat] || stat}</span>
                  <span className="text-white">{value}</span>
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </CardBody>
    </Card>
  );
};

export default EVTraining;
